import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialState = { name: '', email: '', phone: '', subject: '', message: '' };

const ContactForm = () => {
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData(initialState);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'bg-gray-50 border-gray-200 focus:border-brand-red focus:ring-brand-red rounded-lg';

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-charcoal font-semibold mb-2 text-sm">Full Name *</label>
          <Input id="name" name="name" type="text" required value={formData.name} onChange={handleChange}
            disabled={isSubmitting} className={inputClass} placeholder="John Doe" data-testid="contact-name-input" />
        </div>
        <div>
          <label htmlFor="email" className="block text-charcoal font-semibold mb-2 text-sm">Email Address *</label>
          <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange}
            disabled={isSubmitting} className={inputClass} placeholder="john@example.com" data-testid="contact-email-input" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="phone" className="block text-charcoal font-semibold mb-2 text-sm">Phone Number *</label>
          <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange}
            disabled={isSubmitting} className={inputClass} placeholder="+27 XX XXX XXXX" data-testid="contact-phone-input" />
        </div>
        <div>
          <label htmlFor="subject" className="block text-charcoal font-semibold mb-2 text-sm">Subject *</label>
          <Input id="subject" name="subject" type="text" required value={formData.subject} onChange={handleChange}
            disabled={isSubmitting} className={inputClass} placeholder="e.g. Construction project enquiry" data-testid="contact-subject-input" />
        </div>
      </div>

      <div>
        <label htmlFor="message" className="block text-charcoal font-semibold mb-2 text-sm">Message *</label>
        <Textarea id="message" name="message" required rows={5} value={formData.message} onChange={handleChange}
          disabled={isSubmitting}
          className="bg-gray-50 border-gray-200 focus:border-brand-red focus:ring-brand-red resize-none rounded-lg"
          placeholder="Tell us about your project or enquiry..." data-testid="contact-message-input" />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        data-testid="contact-submit-button"
        className="w-full bg-brand-red hover:bg-red-700 text-white font-semibold text-lg py-6 rounded-lg shadow-lg hover:shadow-xl transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Submitting...' : 'Send Message'}
        {!isSubmitting && <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />}
      </Button>
    </form>
  );
};

export default ContactForm;
